/**
 * The brand hub's extra data (web plan W3.6): the backend's `brandPage`
 * for a slug and locale, used in parallel with the entity query
 * (`useBrandDetail`) on `BrandDetailPage`. The entity query supplies
 * promotions/stores/description; this query adds saleHistory and similarBrands.
 *
 * A null slug → skip (returns null hub, status loading). A NOT_FOUND or null
 * result maps to `not_found`; 5xx / network maps to `error`.
 *
 * On a brand page the framework server rendered, its answer is the route's
 * preload (@/hooks/routePreload): shown on the server and in the render that
 * hydrates it, and a guest's browser does not ask again.
 */
import { BRAND_PAGE_HUB } from '@/graphql/queries';
import { usePreloadedQuery } from '@/hooks/routePreload';
import { brandPageHubVariables, type BrandHubPage } from '@/seo/brandHubs';
import type { Locale } from '@/seo/locales';
import { detailStatus, type DetailStatus } from '@/utils/detailStatus';
import { queryErrorMessage } from './useAppData';

export interface BrandPageHubState {
  hub: BrandHubPage | null;
  status: DetailStatus;
  error: string | null;
  refetch: () => void;
}

export function useBrandPageHub(
  slug: string | null,
  locale: Locale,
): BrandPageHubState {
  const { data, loading, error, refetch } = usePreloadedQuery<{ brandPage?: BrandHubPage | null }>(
    BRAND_PAGE_HUB,
    {
      skip: !slug,
      variables: slug ? brandPageHubVariables(slug, locale) : undefined,
      notifyOnNetworkStatusChange: true,
    },
  );
  const hub = data?.brandPage ?? null;
  return {
    hub,
    status: detailStatus({ item: hub, loading, error, validParam: Boolean(slug) }),
    error: queryErrorMessage(error),
    refetch: () => {
      void refetch();
    },
  };
}
